import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CloudSite, InvitationInfo, InviteResponse, MembershipRole, SiteMember } from '@ste/shared';
import { rootFetch, useAuth } from './auth.ts';
import { API_ROOT, useConnection } from './connection.ts';

const json = (body: unknown, method = 'POST') => ({ method, body: JSON.stringify(body) });

/** STE Connect: the sites of the user, refreshed for the online status */
export function useSites() {
  const mode = useConnection((s) => s.mode);
  const authenticated = useAuth((s) => s.status === 'authenticated');
  const setSites = useConnection((s) => s.setSites);
  const query = useQuery({
    queryKey: ['cloud', 'sites'],
    queryFn: () => rootFetch<CloudSite[]>('/sites'),
    enabled: mode === 'cloud' && authenticated,
    refetchInterval: 30_000,
  });
  useEffect(() => {
    if (query.data) setSites(query.data);
  }, [query.data, setSites]);
  return query;
}

export const useMembers = (siteId: string, enabled: boolean) =>
  useQuery({
    queryKey: ['cloud', 'members', siteId],
    queryFn: () => rootFetch<SiteMember[]>(`/sites/${siteId}/members`),
    enabled,
  });

export function useSiteCommands() {
  const client = useQueryClient();
  const refresh = () => void client.invalidateQueries({ queryKey: ['cloud'] });
  return {
    /** STE staff follow the push notifications of a site (ADR-0025) */
    subscribe: useMutation({
      mutationFn: ({ siteId, subscribed }: { siteId: string; subscribed: boolean }) =>
        rootFetch<{ subscribed: boolean }>(`/sites/${siteId}/subscription`, {
          method: 'PUT',
          body: JSON.stringify({ subscribed }),
        }),
      onSettled: refresh,
    }),
    create: useMutation({
      mutationFn: (title: string) => rootFetch<{ id: string; title: string }>('/sites', json({ title })),
      onSuccess: refresh,
    }),
    pair: useMutation({
      mutationFn: ({ siteId, code }: { siteId: string; code: string }) =>
        rootFetch<{ deviceId: string; tunnelIp: string }>(`/sites/${siteId}/pairing`, json({ code })),
      onSuccess: refresh,
    }),
    unpair: useMutation({
      mutationFn: (siteId: string) => rootFetch<void>(`/sites/${siteId}/device`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    invite: useMutation({
      mutationFn: ({ siteId, email, role }: { siteId: string; email: string; role: MembershipRole }) =>
        rootFetch<InviteResponse>(`/sites/${siteId}/invitations`, json({ email, role })),
    }),
    removeMember: useMutation({
      mutationFn: ({ siteId, userId }: { siteId: string; userId: string }) =>
        rootFetch<void>(`/sites/${siteId}/members/${userId}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}

export async function fetchInvitation(token: string): Promise<InvitationInfo> {
  const res = await fetch(`${API_ROOT}/invitations/${encodeURIComponent(token)}`);
  if (!res.ok)
    throw new Error(
      ((await res.json().catch(() => ({}))) as { message?: string }).message ?? 'Einladung ungültig',
    );
  return (await res.json()) as InvitationInfo;
}
